import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tooltip from "@mui/material/Tooltip";
import { useKioskContext } from '../context/KioskContext';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from "sweetalert2";
import Divider from '@mui/material/Divider';
import axios from 'axios';

const DiningLocation = ({ onLocationSelect, onBack, posId }) => {
  const { translate } = useKioskContext();
  const [hasEnabledDelivery, setHasEnabledDelivery] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(true);
  const [hasComplimentary, setHasComplimentary] = useState(false);
  const [complimentaryLoading, setComplimentaryLoading] = useState(true);

  useEffect(() => {
    const checkEnabledDeliveries = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-public-deliveries?posId=${posId}`
        );
        const enabled = res.data?.deliveries?.some((d) => d.toggle);
        setHasEnabledDelivery(enabled);
      } catch (err) {
        console.error('Failed to fetch deliveries', err);
      } finally {
        setDeliveryLoading(false);
      }
    };
  
    checkEnabledDeliveries();
  }, [posId]);

  useEffect(() => {
    const checkComplimentaryMenu = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-complimentary-menu?posId=${posId}`
        );
        const availableMenu = res.data?.availableMenu || [];
        setHasComplimentary(availableMenu.length > 0);
      } catch (err) {
        console.error('Failed to fetch complimentary menu', err);
      } finally {
        setComplimentaryLoading(false);
      }
    };
    checkComplimentaryMenu();
  }, [posId]);

  return (
    <>
      {/* Back Button - positioned at the top left of the viewport */}
      <Box sx={{ position: 'fixed', top: 20, left: 20, zIndex: 10 }}>
        <IconButton 
          onClick={onBack} 
          color="primary"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.default' } }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>
      
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
          backgroundColor: '#f5f5f5',
        }}
      >
   
        {/* Logo and Order Number Button */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom>
            {translate('Select Dining Option', {
              en: 'Select Dining Option',
              zh: '选择用餐选项',
              th: 'เลือกตัวเลือกการรับประทานอาหาร',
              tl: 'Pumili ng Opsyon sa Pagkain'
            })}
          </Typography>
        {/* <Tooltip title="Under Maintenance: Please use Override found at Reports Module">
        <span>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              textAlign: "center",
              cursor: "not-allowed",
              borderRadius: 4,
              transition: "all 0.3s",
              mb: 3,
              bgcolor: "grey.500",
              color: "white",
              "&:hover": {
                transform: "none",
                boxShadow: 3,
                bgcolor: "grey.600",
              },
            }}
            onClick={() => {
              Swal.fire({
                icon: "error",
                title: "Under Maintenance",
                text: "Please use Override found at Reports Module",
                confirmButtonColor: "#d33", // red confirm button
              });
            }}
          >
            <Typography variant="h4" component="h2">
              ORDER NUMBER
            </Typography>
          </Paper>
        </span>
      </Tooltip> */}
        </Box>

        {/* Dining Options */}
        <Grid container spacing={4} justifyContent="center">
          {/* Dine-In Option */}
          <Grid item xs={12} sm={6} md={5} lg={4}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 4,
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 8,
                },
              }}
              onClick={() => onLocationSelect('Dine-In')}
            >
            <img
              src="/assets/images/dinein.png"
              alt="Dine-In"
              style={{ maxWidth: '100%', height: 'auto', marginBottom: 20 }}
            />
            <Typography variant="h4" component="h2">
              {translate('Dine-In', {
                en: 'Dine-In',
                zh: '堂食',
                th: 'ทานที่ร้าน',
                tl: 'Dine In'
              })}
            </Typography>
          </Paper>
        </Grid>
        
        {/* Take-Out Option */}
        <Grid item xs={12} sm={6} md={5} lg={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: 4,
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: 8,
              },
            }}
            onClick={() => onLocationSelect('Take-Out')}
          >
            <img
              src="/assets/images/takeout.png"
              alt="Take-Out"
              style={{ maxWidth: '100%', height: 'auto', marginBottom: 20 }}
            />
            <Typography variant="h4" component="h2">
              {translate('Take-Out', {
                en: 'Take-Out',
                zh: '外带',
                th: 'สั่งกลับบ้าน',
                tl: 'Take Out'
              })}
            </Typography>
          </Paper>
        </Grid>

         {/* Delivery Option (conditionally rendered) */}
         <Grid item xs={12} sm={6} md={5} lg={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: 4,
              cursor: hasEnabledDelivery ? 'pointer' : 'default',
              transition: 'all 0.3s',
              opacity: hasEnabledDelivery ? 1 : 0.5,
              pointerEvents: hasEnabledDelivery ? 'auto' : 'none',
              '&:hover': hasEnabledDelivery
                ? {
                    transform: 'scale(1.05)',
                    boxShadow: 8,
                  }
                : {},
            }}
            onClick={() => hasEnabledDelivery && onLocationSelect('Delivery')}
          >
            <img
              src="/assets/images/delivery.png"
              alt="Delivery"
              style={{ maxWidth: '100%', height: 'auto', marginBottom: 20 }}
            />
            <Typography variant="h4" component="h2">
              {translate('Delivery', {
                en: 'Delivery',
                zh: '送货',
                th: 'จัดส่ง',
                tl: 'Paghahatid',
              })}
            </Typography>

            {deliveryLoading ? (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                  Validating delivery...
                </Box>
              </Typography>
            ) : !hasEnabledDelivery ? (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                No Delivery Enabled
              </Typography>
            ) : null}
          </Paper>
        </Grid>
      </Grid>

      {/* Complimentary */}
      <Grid container spacing={4} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={5} lg={4}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 4,
                cursor: hasComplimentary ? 'pointer' : 'default',
                transition: 'all 0.3s',
                opacity: hasComplimentary ? 1 : 0.5,
                pointerEvents: hasComplimentary ? 'auto' : 'none',
                '&:hover': hasComplimentary ? { transform: 'scale(1.05)', boxShadow: 8 } : {},
              }}
              onClick={() => hasComplimentary && onLocationSelect('Complimentary')}
            >
              <Typography variant="h4" component="h2">
                {translate('Complimentary', {
                  en: 'Complimentary',
                  zh: '免费',
                  th: 'ฟรี',
                  tl: 'Libre',
                })}
              </Typography>

              {complimentaryLoading ? (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                    Checking complimentary...
                  </Box>
                </Typography>
              ) : !hasComplimentary ? (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  No Complimentary Menu
                </Typography>
              ) : null}
            </Paper>
          </Grid>
        </Grid>
    </Box>
    </>
  );
};

export default DiningLocation;
